type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

interface Logger {
  debug: (message: string, context?: Record<string, unknown>) => void;
  info: (message: string, context?: Record<string, unknown>) => void;
  warn: (message: string, context?: Record<string, unknown>) => void;
  error: (message: string, context?: Record<string, unknown>) => void;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function createLogger(): Logger {
  const isDev = import.meta.env.DEV;
  const minLevel: LogLevel = isDev ? "debug" : "warn";

  const shouldLog = (level: LogLevel): boolean => {
    // eslint-disable-next-line security/detect-object-injection -- level and minLevel are typed LogLevel literals
    return LOG_LEVELS[level] >= LOG_LEVELS[minLevel];
  };

  const formatEntry = (entry: LogEntry): string => {
    const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : "";
    return `[${entry.timestamp}] ${entry.level.toUpperCase()}: ${entry.message}${contextStr}`;
  };

  const log = (
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
  ): void => {
    if (!shouldLog(level)) return;

    const sanitizedContext = sanitizeContext(context);
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...(sanitizedContext !== undefined && { context: sanitizedContext }),
    };

    // In production, only console.warn and console.error
    // In development, use appropriate console methods
    if (isDev) {
      const formatted = formatEntry(entry);
      switch (level) {
        case "debug":
          console.debug(formatted);
          break;
        case "info":
          console.info(formatted);
          break;
        case "warn":
          console.warn(formatted);
          break;
        case "error":
          console.error(formatted);
          break;
      }
    } else {
      // Production: minimal logging, could be sent to external service
      if (level === "error") {
        console.error(`[ERROR] ${message}`);
      } else if (level === "warn") {
        console.warn(`[WARN] ${message}`);
      }
    }
  };

  return {
    debug: (message, context) => log("debug", message, context),
    info: (message, context) => log("info", message, context),
    warn: (message, context) => log("warn", message, context),
    error: (message, context) => log("error", message, context),
  };
}

/**
 * Sanitize context to remove sensitive data
 */
function sanitizeContext(
  context?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!context) return undefined;

  const sensitiveKeys = [
    "password",
    "token",
    "accessToken",
    "refreshToken",
    "secret",
    "apiKey",
    "authorization",
  ];

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(context)) {
    if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
      sanitized[key] = "[REDACTED]"; // eslint-disable-line security/detect-object-injection -- key is from Object.entries iteration
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeContext(value as Record<string, unknown>); // eslint-disable-line security/detect-object-injection -- key is from Object.entries iteration
    } else {
      sanitized[key] = value; // eslint-disable-line security/detect-object-injection -- key is from Object.entries iteration
    }
  }

  return sanitized;
}

export const logger = createLogger();
